<script lang="ts">
interface Props {
    tourShipperCompanyId?: uuid | null
    tourCompanyId?: uuid
    hideDatePicker?: boolean
    showTour?: boolean
    boxed?: boolean
    pickedShipmentIds?: uuid[]
    filter?: FetchShipmentsFilter
    withCollis?: boolean
}
</script>
<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue'
import axios, { AxiosRequestConfig } from 'axios'
import { useI18n } from 'vue-i18n'
import { debouncedWatch } from '@vueuse/core'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { uuid, PaginatedResponse } from '@/types/general'
import { addressString } from '@/utils/string'
import { datetime } from '@/utils/dates'
import { type PaginationData } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { FetchShipmentsFilter, MinimalShipment, ShipmentType } from '@/types/delivery-management'
import { shipmentTypes } from '@/utils/delivery-management'

import ShipmentModal from '@/components/delivery-management/ShipmentModal.vue'
import PaginationFooter from '@/components/PaginationFooter.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MySearchInput from '@/components/my-components/form/MySearchInput.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'

const props = withDefaults(defineProps<Props>(), {
    boxed: true,
    showTour: false,
    hideDatePicker: false,
    pickedShipmentIds: () => [],
    withCollis: false,
})
const emit = defineEmits<{
    (e: 'shipmentSelected', value: MinimalShipment): void
}>()

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

const paginationData = ref<PaginationData>({
    from: 1,
    to: 1,
    perPage: 5,
    lastPage: 1,
    currentPage: Number(route.query.page ?? 1),
    total: 0,
})
const shipments = ref<MinimalShipment[]>([])
const searchQuery = ref('')
const loading = ref(false)
const selectedShipmentId = ref<string>()
const shipmentModalOpen = ref(false)
const showFilters = ref(false)
const date = ref<Date[]>([
    dayjs().subtract(6, 'days').startOf('day').toDate(),
    dayjs().endOf('day').toDate(),
])

async function fetchAvailableShipments(page?: number) {
    loading.value = true
    const filter = { ...props.filter }
    if (!props.hideDatePicker && date.value[0] && date.value[1]) {
        filter['start-date'] = dayjs(date.value[0]).startOf('day').toISOString()
        filter['end-date'] = dayjs(date.value[1]).endOf('day').toISOString()
    }

    const params: AxiosRequestConfig['params'] = {
        filter: filter,
        query: searchQuery.value,
        page: page ?? paginationData.value.currentPage,
        shipperCompanyId: props.tourShipperCompanyId,
        'per-page': 5,
        simple: true,
        'with-collis': props.withCollis,
    }
    const response = await axios.get<PaginatedResponse<MinimalShipment>>(
        window.route('dm.company.shipments.index', {
            company: props.tourCompanyId ?? authStore.companyId,
        }),
        { params: params },
    )

    shipments.value = response.data.data

    paginationData.value = {
        from: response.data.meta.from,
        to: response.data.meta.to,
        perPage: response.data.meta.per_page,
        lastPage: response.data.meta.last_page,
        currentPage: response.data.meta.current_page,
        total: response.data.meta.total,
    }

    loading.value = false
}

async function selectShipment(shipment: MinimalShipment) {
    emit('shipmentSelected', shipment)
}

function showShipment(shipmentId: string) {
    selectedShipmentId.value = shipmentId
    shipmentModalOpen.value = true
}

function getShipmentDestinationName(shipment: MinimalShipment): string {
    if (shipment.type === ShipmentType.Pickup) {
        return shipment.sendingCompany.name
    }

    return shipment.receivingCompany.name
}

function getShipmentAddress(shipment: MinimalShipment): string {
    if (shipment.address) return shipment.address

    if (shipment.type === ShipmentType.Pickup) {
        return addressString(shipment.sendingLocation)
    }

    return addressString(shipment.receivingCompany)
}

debouncedWatch(
    () => searchQuery.value,
    (value) => fetchAvailableShipments(),
    { debounce: 500 },
)

watch(
    () => date.value,
    () => fetchAvailableShipments(),
    { deep: true },
)

watch(
    () => [props.tourCompanyId, props.filter, props.tourShipperCompanyId],
    () => fetchAvailableShipments(),
    { deep: true },
)

onBeforeMount(() => fetchAvailableShipments())

defineExpose({
    fetchAvailableShipments,
})
</script>

<template>
    <div
        class="flex flex-col w-full justify-start"
        :class="{ 'px-4 rounded-xl border border-stone-300 dark:border-dark-500': props.boxed }"
    >
        <LoaderWrapper :visible="loading" class="rounded-xl" />

        <div class="w-full flex self-center mt-3 space-x-3">
            <MySearchInput v-model="searchQuery" :class="{ ' dark:bg-dark-600': !props.boxed }">
                <template v-if="$slots.filters" #inputSuffix>
                    <mdi:filter-outline
                        v-if="!showFilters"
                        class="text-sm cursor-pointer"
                        @click="showFilters = !showFilters"
                    />
                    <mdi:filter
                        v-else
                        class="text-sm cursor-pointer"
                        @click="showFilters = !showFilters"
                    />
                </template>
            </MySearchInput>

            <MyDateRangePicker v-if="!props.hideDatePicker" v-model="date" use-input-height />
        </div>

        <slot v-if="showFilters" name="filters" />

        <div class="mt-4 flex flex-col h-full space-y-3">
            <div
                v-if="shipments.length === 0 && !loading"
                class="flex h-full items-center justify-center dark:text-primary-300 py-12"
            >
                {{ t('noAvailableShipments') }}
            </div>

            <slot name="shipments" :shipments="shipments">
                <div
                    v-for="shipment in shipments"
                    :key="shipment.id"
                    class="flex justify-between space-x-3 w-full rounded-xl bg-white p-3 shadow-md dark:bg-dark-400"
                    :class="{ 'opacity-50': pickedShipmentIds.includes(shipment.id) }"
                >
                    <div class="flex w-2/5 flex-col">
                        <span class="text-sm dark:text-primary-50 font-semibold">
                            {{ t('shipment') }}
                        </span>
                        <div class="flex">
                            <span class="flex text-sm dark:text-primary-50 mr-1 break-all">
                                {{ shipment.shipmentNumber ?? shipment.name ?? '-' }}
                                <mdi:eye
                                    v-if="shipment && !pickedShipmentIds.includes(shipment.id)"
                                    class="ml-1 cursor-pointer hover:text-primary-400 dark:hover:text-primary-300 min-h-5 min-w-5"
                                    @click="showShipment(shipment!.id)"
                                />
                            </span>
                        </div>

                        <span
                            class="mr-2 text-sm dark:text-primary-50"
                            v-text="t(shipmentTypes[shipment.type])"
                        />
                        <span class="text-xs">{{ datetime(shipment.plannedAt) }}</span>
                    </div>
                    <div class="flex w-2/5 flex-col">
                        <span class="text-sm dark:text-primary-50 font-semibold truncate">
                            {{
                                shipment.type === ShipmentType.Pickup ? t('sender') : t('receiver')
                            }}
                        </span>
                        <span
                            v-truncate-tooltip="getShipmentDestinationName(shipment)"
                            class="text-sm dark:text-primary-50 truncate"
                            v-text="getShipmentDestinationName(shipment)"
                        />
                        <div
                            class="text-xs dark:text-primary-200 break-words"
                            v-text="getShipmentAddress(shipment)"
                        />
                    </div>

                    <div v-if="props.showTour" class="flex w-1/4 flex-col">
                        <span class="text-sm dark:text-primary-50 font-semibold">
                            {{ t('tour') }}
                        </span>
                        <span class="text-xs dark:text-primary-200">
                            {{ shipment.tour?.name ?? t('unassigned') }}
                        </span>
                    </div>

                    <div class="flex flex-shrink-0 items-center justify-end">
                        <MyButton
                            v-if="!pickedShipmentIds.includes(shipment.id)"
                            scheme="primary"
                            type="button"
                            size="small"
                            @click="selectShipment(shipment)"
                        >
                            {{ t('select') }}
                        </MyButton>
                        <MyButton v-else scheme="primary" type="button" size="small" disabled>
                            {{ t('added') }}
                        </MyButton>
                    </div>
                </div>
            </slot>

            <PaginationFooter
                class="border-t border-primary-200 dark:border-dark-500"
                hide-per-page
                :pagination-data="paginationData"
                :refetch="(params) => fetchAvailableShipments(params?.page ?? 1)"
                :row-count="shipments.length ?? 0"
            />
        </div>

        <ShipmentModal
            v-if="selectedShipmentId"
            :id="selectedShipmentId"
            v-model:open="shipmentModalOpen"
            @update:open="selectedShipmentId = undefined"
        />
    </div>
</template>
